import type { ColumnFilterOption } from '@elementary/common-ui/components/Table/types';
import type { IncidentV2StatusCounts } from '@elementary/common-ui/services/gql/types';
import {
  ASSIGNEE_COLUMN_ID,
  COLUMN_TO_INCIDENT_FIELD_MAP,
  NO_ASSIGNEE_VALUE,
  STATUS_COLUMN_ID,
  STATUS_GROUP_VALUES,
} from '../constants';
import type { IncidentFilterOptionsGroup } from '../types';

const STATUS_COUNT_KEY: Record<
  typeof STATUS_GROUP_VALUES[number],
  keyof Omit<IncidentV2StatusCounts, '__typename'>
> = {
  Open: 'open',
  Acknowledged: 'acknowledged',
  Resolved: 'resolved',
};

export const getIncidentFieldOptions = (
  filterOptions: IncidentFilterOptionsGroup[] | undefined,
  field: string,
) =>
  filterOptions?.find(
    (group) => group.field.toLowerCase() === field.toLowerCase(),
  )?.options ?? [];

type Args = {
  groupByColumnId: string;
  groupOptions: IncidentFilterOptionsGroup[] | undefined;
  statusCounts: IncidentV2StatusCounts | undefined;
};

export const getIncidentGroupValues = ({
  groupByColumnId,
  groupOptions,
  statusCounts,
}: Args): ColumnFilterOption[] => {
  if (groupByColumnId === STATUS_COLUMN_ID) {
    return STATUS_GROUP_VALUES.map((value) => ({
      label: value,
      value,
      count: statusCounts?.[STATUS_COUNT_KEY[value]],
    }));
  }

  const field = COLUMN_TO_INCIDENT_FIELD_MAP[groupByColumnId];
  if (!field || !groupOptions) return [];

  const options = getIncidentFieldOptions(groupOptions, field).map(
    (option) => ({
      label: option.value,
      value: option.value,
      count: option.count,
    }),
  );

  if (groupByColumnId !== ASSIGNEE_COLUMN_ID) return options;

  const total = getIncidentFieldOptions(
    groupOptions,
    COLUMN_TO_INCIDENT_FIELD_MAP[STATUS_COLUMN_ID],
  ).reduce((sum, option) => sum + option.count, 0);
  const assignedTotal = options.reduce((sum, option) => sum + option.count, 0);

  return [
    {
      label: 'No Assignee',
      value: NO_ASSIGNEE_VALUE,
      count: total - assignedTotal,
    },
    ...options.filter((option) => option.value !== NO_ASSIGNEE_VALUE),
  ];
};
